import { StringUtility } from '@common/utils';
import { DateTime } from 'luxon';
import {} from './AppointmentTypeModel';
import {
  CommunicationType,
  DiscountItemTypes,
  ReservationAssignmentStatuses,
  SalesItemType,
  VisitNoteType,
  VisitStatus,
} from './Enums';
import {
  AppointmentTypeModel,
  ClientModel,
  FollowUpAppointmentModel,
  HospitalPrescriptionModel,
  IAppointmentTypeModel,
  IClientModel,
  IHospitalPrescriptionModel,
  IIntakeModel,
  ILabOrderModel,
  IntakeModel,
  IPetModel,
  IPhotoModel,
  IReservationModel,
  IStorageContentModel,
  IVisitAnesthesiaFormModel,
  IVisitAttachmentModel,
  IVisitCompletionTypeModel,
  IVisitConsentModel,
  IVisitDiagnosisModel,
  IVisitEventModel,
  IVisitExamResultModel,
  IVisitNoteModel,
  IVisitSectionVersionModel,
  IVisitStatusModel,
  IVisitWellnessExamModel,
  IPetDiagnosisModel,
  LabOrderModel,
  PetModel,
  PhotoModel,
  ReservationModel,
  StorageContentModel,
  VisitAnesthesiaFormModel,
  VisitAttachmentModel,
  VisitCompletionTypeModel,
  VisitConsentModel,
  VisitDiagnosisModel,
  VisitEventModel,
  VisitExamResultModel,
  VisitNoteModel,
  VisitReferralModel,
  VisitSalesItemModel,
  VisitSectionVersionModel,
  VisitSiblingModel,
  VisitStatusModel,
  VisitWellnessExamModel,
  PetPreviousVisitModel,
  PetDiagnosisModel,
} from '@common/models';
import { action, makeObservable, observable, computed } from 'mobx';
import { ISyncModel, SyncModel } from '@common/models/SyncModel';

export interface IVisitModel extends ISyncModel {
  visitId: number;
  visitPublicId: string | undefined;
  cashPayment: number;
  checkPayment: number;
  clinicId: number;
  clinicStoreId: number;
  consultationValidationStatusId: number;
  consultationHasBeenValidated: boolean;
  visitDate: string | undefined;
  fleaTickProductId: number | undefined;
  hasMicrochip: boolean | undefined;
  hasOptedOutOfPackage: boolean;
  hasOutsideContact: boolean | undefined;
  heartwormProductId: number | undefined;
  isExpress: boolean;
  isRxOnly: boolean;
  isPetNotPresent: boolean | undefined;
  isPetPregnant: boolean | undefined;
  lastVaccineDate: string | undefined;
  lastRabiesDate: string | undefined;
  paymentMethodId: number | undefined;
  petId: number;
  veterinarianSignaturePhotoId: number | undefined;
  vetServicesHasBeenValidated: boolean;
  vetServicesValidationStatusId: number;
  visitCompletionTypeId: number;
  visitStatusId: number;
  visitSummaryDocumentId: number | undefined;
  smsStatus: number;
  hasVetAttestation: boolean;
  hasPetAttestation: boolean;
  room: string | undefined;
  visitWellnessExam: IVisitWellnessExamModel;
  visitDiagnoses: IVisitDiagnosisModel[];
  petDiagnoses: IPetDiagnosisModel[];
  reasonForVisit: string | undefined;
  visitHistory: string;
  visitSubjective: string;
  normalResponses: string;
  abnormalResponses: string;
  unableToExamResponses: string;
  freeNonPackageSalesItems: VisitSalesItemModel[];
  addOnSalesItems: VisitSalesItemModel[];
  paidNonPackageSalesItems: VisitSalesItemModel[];
  discountAndRebateSalesItems: VisitSalesItemModel[];
  additionalInfoLabels: string[];
  siblings: VisitSiblingModel[];
  showRabiesDisclaimer: boolean;
  vetAssistantFirstLast: string;
  brandId: number;
  clinicStateId: number;
  clinicAllowChangingSingleVisitVet: boolean;
  marketId: number;
  firstVisitDate: string | undefined;
  lastVisitDate: string | undefined;
  checkinStart: string | undefined;
  consultationStart: string | undefined;
  vetServicesStart: string | undefined;
  checkoutStart: string | undefined;
  clientSignaturePhotoId: number | undefined;
  rabiesCertificateDocumentId: number | undefined;
  prescriptionDocumentId: number | undefined;
  partnerId: number;
  visitStatus: IVisitStatusModel;
  // consultationValidationStatus: ValidationStatusModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
  // vetServicesValidationStatus: ValidationStatusModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
  pet: IPetModel;
  client: IClientModel;
  visitEvent: IVisitEventModel;
  historicalVisitWellnessExams: IVisitWellnessExamModel[] | undefined;
  // paymentMethod: PaymentMethodModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
  visitSalesItems: VisitSalesItemModel[];
  veterinarianSignaturePhoto: IPhotoModel | undefined;
  clinicVetSignaturePhoto: IPhotoModel | undefined;
  clientSignaturePhoto: IPhotoModel | undefined;
  visitSummaryDocument: IStorageContentModel | undefined;
  rabiesCertificateDocument: IStorageContentModel | undefined;
  prescriptionDocument: IStorageContentModel | undefined;
  // heartwormProduct: HeartwormProductModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
  // fleaTickProduct: FleaTickProductModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
  salesPackageId: number | undefined;
  salesPackagePrice: number | undefined;
  salesPackageName: string | undefined;
  salesPackageDiscountAmount: number | undefined;
  salesPackageInvoicePrice: number | undefined;
  salesPackageSalesSubCategoryId: number | undefined;
  salesPackageSalesSubCategoryName: string;
  salesPackageSpeciesId: number | undefined;
  salesPackageSpeciesName: string;
  salesPackageDisplayName: string;
  salesPackageDescription: string;
  salesPackageCode: string;
  salesPackageHelpPhotoId: number | undefined;
  salesPackageSortOrder: number | undefined;
  salesPackageExpirationDays: number | undefined;
  salesPackageVaccineReminderDefaultDays: number | undefined;
  isSalesPackageRefunded: boolean;
  visitCompletionType: IVisitCompletionTypeModel;
  serviceAmount: number;
  productAmount: number;
  licenseFeeAmount: number;
  vetcoPrescriptionAmount: number;
  discountAmount: number;
  taxPercentage: number;
  subtotalPreDiscountsAmount: number;
  subtotalPostDiscountsAmount: number;
  taxAmount: number;
  refundAmount: number;
  taxableTotalAmount: number;
  nonTaxableTotalAmount: number;
  totalAmount: number;
  servicesOnlyDiscountAmount: number;
  productsOnlyDiscountAmount: number;
  servicesRebateAmount: number | undefined;
  productsRebateAmount: number | undefined;
  totalRebateAmount: number | undefined;
  nonTaxableFeeAmount: number | undefined;
  taxableFeeAmount: number | undefined;
  totalFeeAmount: number | undefined;
  totalAOVAmount: number | undefined;
  veterinarianId: number | undefined;
  veterinarianFirstName: string;
  veterinarianLastName: string;
  veterinarianLicenseNumber: string;
  veterinarianLicenseStateId: number | undefined;
  veterinarianLicenseStateAbbreviation: string;
  veterinarianFullName: string;
  veterinarianLicenseExpirationDate: string | undefined;
  veterinarianEmail: string;
  dispensingAuthUserId: number | undefined;
  dispensingUserFirstName: string | undefined;
  dispensingUserLastName: string | undefined;
  thankYouNote: string;
  storeStateNote: string;
  clientStateNote: string;
  visitTagId: number | undefined;
  // visitTag: VisitTagModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
  reservationId: number | undefined;
  reservation: IReservationModel | undefined;
  examResults: IVisitExamResultModel[] | undefined;
  visitNotes: IVisitNoteModel[] | undefined;
  referrals: VisitReferralModel[];
  hasEvaluatedAutoRecommendations: boolean;
  intakeId: number | undefined;
  intake: IIntakeModel;
  arrived: string | undefined;
  firstCheckIn: string | undefined;
  visitStarted: string | undefined;
  arrivedWaitingTime: number | undefined;
  checkInWaitingTime: number | undefined;
  intakeCompletionStatusId: number | undefined;
  // intakeCompletionStatus: IntakeCompletionStatusModel; // TODO JFH: Not immediately needed. Leaving out for now to reduce scope of work.
  consents: IVisitConsentModel[];
  needsProactiveFleaTickPrescription: boolean | undefined;
  appointmentType: IAppointmentTypeModel | undefined;
  notes: string | undefined;
  dateCreated: string;
  dateUpdated: string;
  visitAttachments: IVisitAttachmentModel[];
  visitSectionVersions: IVisitSectionVersionModel[];
  labOrders: ILabOrderModel[];
  anesthesiaForms: IVisitAnesthesiaFormModel[];
  hospitalPrescriptions: IHospitalPrescriptionModel[];
  isInAdminMode: boolean;
  audioRecordingUsed: boolean;
  followUpBookingURL: string | undefined;
  hasDetailedAdjustments: boolean;
  isVisitLocked: boolean;
  petPreviousVisits: PetPreviousVisitModel[];
  hasSentConfirmationSMS: boolean;
  hasSentConsult1SMS: boolean;
  hasSentConsult2SMS: boolean;
}

export class VisitModel
  extends SyncModel
  implements Record<keyof IVisitModel, any>
{
  @observable id: number; // for json-server
  @observable visitId: number;
  @observable visitPublicId: string | undefined;
  @observable cashPayment: number;
  @observable checkPayment: number;
  @observable clinicId: number;
  @observable clinicStoreId: number;
  @observable consultationValidationStatusId: number;
  @observable consultationHasBeenValidated: boolean;
  @observable visitDate: DateTime | undefined;
  @observable fleaTickProductId: number | undefined;
  @observable hasMicrochip: boolean | undefined;
  @observable hasOptedOutOfPackage: boolean;
  @observable hasOutsideContact: boolean | undefined;
  @observable heartwormProductId: number | undefined;
  @observable isExpress: boolean;
  @observable isRxOnly: boolean;
  @observable isPetNotPresent: boolean | undefined;
  @observable isPetPregnant: boolean | undefined;
  @observable lastVaccineDate: DateTime | undefined;
  @observable lastRabiesDate: DateTime | undefined;
  @observable paymentMethodId: number | undefined;
  @observable petId: number;
  @observable veterinarianSignaturePhotoId: number | undefined;
  @observable vetServicesHasBeenValidated: boolean;
  @observable vetServicesValidationStatusId: number;
  @observable visitCompletionTypeId: number;
  @observable visitStatusId: number;
  @observable visitSummaryDocumentId: number | undefined;
  @observable smsStatus: number;
  @observable hasVetAttestation: boolean;
  @observable hasPetAttestation: boolean;
  @observable room: string | undefined;
  @observable visitWellnessExam: VisitWellnessExamModel;
  @observable visitDiagnoses: VisitDiagnosisModel[];
  @observable petDiagnoses: PetDiagnosisModel[];
  @observable reasonForVisit: string | undefined;
  @observable visitHistory: string;
  @observable visitSubjective: string;
  @observable normalResponses: string;
  @observable abnormalResponses: string;
  @observable unableToExamResponses: string;
  @observable additionalInfoLabels: string[];
  @observable siblings: VisitSiblingModel[];
  @observable showRabiesDisclaimer: boolean;
  @observable vetAssistantFirstLast: string;
  @observable brandId: number;
  @observable clinicStateId: number;
  @observable clinicAllowChangingSingleVisitVet: boolean;
  @observable marketId: number;
  @observable firstVisitDate: DateTime | undefined;
  @observable lastVisitDate: DateTime | undefined;
  @observable checkinStart: DateTime | undefined;
  @observable consultationStart: DateTime | undefined;
  @observable vetServicesStart: DateTime | undefined;
  @observable checkoutStart: DateTime | undefined;
  @observable clientSignaturePhotoId: number | undefined;
  @observable rabiesCertificateDocumentId: number | undefined;
  @observable prescriptionDocumentId: number | undefined;
  @observable partnerId: number;
  @observable visitStatus: VisitStatusModel;
  @observable pet: PetModel;
  @observable client: ClientModel;
  @observable visitEvent: VisitEventModel;
  @observable historicalVisitWellnessExams: VisitWellnessExamModel[];
  @observable visitSalesItems: VisitSalesItemModel[];
  @observable veterinarianSignaturePhoto: PhotoModel | undefined;
  @observable clinicVetSignaturePhoto: PhotoModel | undefined;
  @observable clientSignaturePhoto: PhotoModel | undefined;
  @observable visitSummaryDocument: StorageContentModel | undefined;
  @observable rabiesCertificateDocument: StorageContentModel | undefined;
  @observable prescriptionDocument: StorageContentModel | undefined;
  @observable salesPackageId: number | undefined;
  @observable salesPackagePrice: number | undefined;
  @observable salesPackageName: string | undefined;
  @observable salesPackageDiscountAmount: number | undefined;
  @observable salesPackageInvoicePrice: number | undefined;
  @observable salesPackageSalesSubCategoryId: number | undefined;
  @observable salesPackageSalesSubCategoryName: string;
  @observable salesPackageSpeciesId: number | undefined;
  @observable salesPackageSpeciesName: string;
  @observable salesPackageDisplayName: string;
  @observable salesPackageDescription: string;
  @observable salesPackageCode: string;
  @observable salesPackageHelpPhotoId: number | undefined;
  @observable salesPackageSortOrder: number | undefined;
  @observable salesPackageExpirationDays: number | undefined;
  @observable salesPackageVaccineReminderDefaultDays: number | undefined;
  @observable isSalesPackageRefunded: boolean;
  @observable visitCompletionType: VisitCompletionTypeModel;
  @observable serviceAmount: number;
  @observable productAmount: number;
  @observable licenseFeeAmount: number;
  @observable vetcoPrescriptionAmount: number;
  @observable discountAmount: number;
  @observable taxPercentage: number;
  @observable subtotalPreDiscountsAmount: number;
  @observable subtotalPostDiscountsAmount: number;
  @observable taxAmount: number;
  @observable refundAmount: number;
  @observable taxableTotalAmount: number;
  @observable nonTaxableTotalAmount: number;
  @observable totalAmount: number;
  @observable servicesOnlyDiscountAmount: number;
  @observable productsOnlyDiscountAmount: number;
  @observable servicesRebateAmount: number | undefined;
  @observable productsRebateAmount: number | undefined;
  @observable totalRebateAmount: number | undefined;
  @observable nonTaxableFeeAmount: number | undefined;
  @observable taxableFeeAmount: number | undefined;
  @observable totalFeeAmount: number | undefined;
  @observable totalAOVAmount: number | undefined;
  @observable veterinarianId: number | undefined;
  @observable veterinarianFirstName: string;
  @observable veterinarianLastName: string;
  @observable veterinarianLicenseNumber: string;
  @observable veterinarianLicenseStateId: number | undefined;
  @observable veterinarianLicenseStateAbbreviation: string;
  @observable veterinarianFullName: string;
  @observable veterinarianLicenseExpirationDate: DateTime | undefined;
  @observable veterinarianEmail: string;
  @observable dispensingAuthUserId: number | undefined;
  @observable dispensingUserFirstName: string | undefined;
  @observable dispensingUserLastName: string | undefined;
  @observable thankYouNote: string;
  @observable storeStateNote: string;
  @observable clientStateNote: string;
  @observable visitTagId: number | undefined;
  @observable reservationId: number | undefined;
  @observable reservation: ReservationModel | undefined;
  @observable examResults: VisitExamResultModel[];
  @observable visitNotes: VisitNoteModel[] | undefined;
  @observable hasEvaluatedAutoRecommendations: boolean;
  @observable intakeId: number | undefined;
  @observable intake: IntakeModel;
  @observable arrived: DateTime | undefined;
  @observable firstCheckIn: DateTime | undefined;
  @observable visitStarted: DateTime | undefined;
  @observable arrivedWaitingTime: number | undefined;
  @observable checkInWaitingTime: number | undefined;
  @observable intakeCompletionStatusId: number | undefined;
  @observable needsProactiveFleaTickPrescription: boolean | undefined;
  @observable appointmentType: AppointmentTypeModel | undefined;
  @observable notes: string | undefined;
  @observable dateCreated: DateTime;
  @observable dateUpdated: DateTime;
  @observable visitAttachments: VisitAttachmentModel[];
  @observable consents: VisitConsentModel[];
  @observable visitSectionVersions: VisitSectionVersionModel[];
  @observable labOrders: LabOrderModel[];
  @observable anesthesiaForms: VisitAnesthesiaFormModel[];
  @observable hospitalPrescriptions: HospitalPrescriptionModel[];
  @observable isInAdminMode: boolean;
  @observable audioRecordingUsed: boolean;
  @observable freeNonPackageSalesItems: VisitSalesItemModel[];
  @observable addOnSalesItems: VisitSalesItemModel[];
  @observable paidNonPackageSalesItems: VisitSalesItemModel[];
  @observable discountAndRebateSalesItems: VisitSalesItemModel[];
  @observable referrals: VisitReferralModel[];
  @observable followUpBookingURL: string | undefined;
  @observable hasDetailedAdjustments: boolean;
  @observable petPreviousVisits: PetPreviousVisitModel[];
  constructor(params: IVisitModel) {
    super(params);

    this.visitId = params.visitId ?? 0;
    this.visitPublicId = StringUtility.SanitizeString(params.visitPublicId);
    this.cashPayment = params.cashPayment ?? 0;
    this.checkPayment = params.checkPayment ?? 0;
    this.clinicId = params.clinicId ?? 0;
    this.clinicStoreId = params.clinicStoreId ?? 0;
    this.consultationHasBeenValidated =
      params.consultationHasBeenValidated ?? false;
    this.consultationValidationStatusId =
      params.consultationValidationStatusId ?? 0;
    this.visitDate = StringUtility.SanitizeDateTimeISOAsUtc(params.visitDate);
    this.fleaTickProductId = StringUtility.SanitizeNumber(
      params.fleaTickProductId,
    );
    this.hasMicrochip = params.hasMicrochip ?? false;
    this.hasOptedOutOfPackage = params.hasOptedOutOfPackage ?? false;
    this.hasOutsideContact = StringUtility.SanitizeBool(
      params.hasOutsideContact,
    );
    this.heartwormProductId = StringUtility.SanitizeNumber(
      params.heartwormProductId,
    );
    this.isExpress = params.isExpress ?? false;
    this.isRxOnly = params.isRxOnly ?? false;
    this.isPetNotPresent = StringUtility.SanitizeBool(params.isPetNotPresent);
    this.isPetPregnant = StringUtility.SanitizeBool(params.isPetPregnant);
    this.lastVaccineDate = StringUtility.SanitizeDateTimeISO(
      params.lastVaccineDate,
    );
    this.lastRabiesDate = StringUtility.SanitizeDateTimeISO(
      params.lastRabiesDate,
    );
    this.paymentMethodId = StringUtility.SanitizeNumber(params.paymentMethodId);
    this.petId = params.petId ?? 0;
    this.veterinarianSignaturePhotoId = StringUtility.SanitizeNumber(
      params.veterinarianSignaturePhotoId,
    );
    this.vetServicesHasBeenValidated =
      params.vetServicesHasBeenValidated ?? false;
    this.vetServicesValidationStatusId =
      params.vetServicesValidationStatusId ?? 0;
    this.visitCompletionTypeId = params.visitCompletionTypeId ?? 0;
    this.visitStatusId = params.visitStatusId ?? 0;
    this.smsStatus = params.smsStatus ?? 0;
    this.hasVetAttestation = params.hasVetAttestation ?? false;
    this.hasPetAttestation = params.hasPetAttestation ?? false;
    this.room = StringUtility.SanitizeString(params.room);
    this.visitWellnessExam = new VisitWellnessExamModel(
      params.visitWellnessExam,
    );
    this.visitDiagnoses = (params.visitDiagnoses ?? []).map(
      (vd) => new VisitDiagnosisModel(vd),
    );
    this.petDiagnoses = (params.petDiagnoses ?? []).map(
      (pd) => new PetDiagnosisModel(pd),
    );
    this.reasonForVisit = StringUtility.SanitizeString(params.reasonForVisit);
    this.notes = StringUtility.SanitizeString(params.notes);

    this.visitHistory = params.visitHistory;
    this.visitSubjective = params.visitSubjective;
    this.normalResponses = params.normalResponses;
    this.abnormalResponses = params.abnormalResponses;
    this.unableToExamResponses = params.unableToExamResponses;
    this.additionalInfoLabels = params.additionalInfoLabels;
    this.siblings = params.siblings;
    this.showRabiesDisclaimer = params.showRabiesDisclaimer;
    this.vetAssistantFirstLast = params.vetAssistantFirstLast;
    this.id = params.visitId;
    this.brandId = params.brandId;
    this.clinicStateId = params.clinicStateId;
    this.clinicAllowChangingSingleVisitVet =
      params.clinicAllowChangingSingleVisitVet;
    this.marketId = params.marketId;

    // vist start event times
    this.firstVisitDate = StringUtility.SanitizeDateTimeISO(
      params.firstVisitDate,
    );
    this.lastVisitDate = StringUtility.SanitizeDateTimeISO(
      params.lastVisitDate,
    );
    this.checkinStart = StringUtility.SanitizeDateTimeISO(params.checkinStart);
    this.consultationStart = StringUtility.SanitizeDateTimeISO(
      params.consultationStart,
    );
    this.vetServicesStart = StringUtility.SanitizeDateTimeISO(
      params.vetServicesStart,
    );
    this.checkoutStart = StringUtility.SanitizeDateTimeISO(
      params.checkoutStart,
    );

    this.rabiesCertificateDocumentId = StringUtility.SanitizeNumber(
      params.rabiesCertificateDocumentId,
    );
    this.prescriptionDocumentId = StringUtility.SanitizeNumber(
      params.prescriptionDocumentId,
    );
    this.partnerId = params.partnerId;
    this.visitStatus = new VisitStatusModel(params.visitStatus);
    this.pet = new PetModel(params.pet);
    this.client = new ClientModel(params.client);
    this.visitEvent = new VisitEventModel(params.visitEvent);
    this.historicalVisitWellnessExams = (
      params.historicalVisitWellnessExams ?? []
    ).map((e) => new VisitWellnessExamModel(e));
    this.visitSalesItems = (params.visitSalesItems ?? []).map(
      (si) => new VisitSalesItemModel(si, this.visitDate),
    );
    this.veterinarianSignaturePhoto =
      params.veterinarianSignaturePhotoId && params.veterinarianSignaturePhoto
        ? new PhotoModel(params.veterinarianSignaturePhoto)
        : undefined;
    this.clinicVetSignaturePhoto = params.clinicVetSignaturePhoto
      ? new PhotoModel(params.clinicVetSignaturePhoto)
      : undefined;
    this.clientSignaturePhoto = params.clientSignaturePhoto
      ? new PhotoModel(params.clientSignaturePhoto)
      : undefined;
    this.visitSummaryDocument = params.visitSummaryDocument
      ? new StorageContentModel(params.visitSummaryDocument)
      : undefined;
    this.rabiesCertificateDocument = params.rabiesCertificateDocument
      ? new StorageContentModel(params.rabiesCertificateDocument)
      : undefined;
    this.prescriptionDocument = params.prescriptionDocument
      ? new StorageContentModel(params.prescriptionDocument)
      : undefined;
    this.salesPackageId = StringUtility.SanitizeNumber(params.salesPackageId);
    this.salesPackagePrice = StringUtility.SanitizeNumber(
      params.salesPackagePrice,
    );
    this.salesPackageDiscountAmount = params.salesPackageDiscountAmount;
    this.salesPackageInvoicePrice = params.salesPackageInvoicePrice;
    this.salesPackageName = params.salesPackageName;
    this.salesPackageSalesSubCategoryId = StringUtility.SanitizeNumber(
      params.salesPackageSalesSubCategoryId,
    );
    this.salesPackageSalesSubCategoryName =
      params.salesPackageSalesSubCategoryName;
    this.salesPackageSpeciesId = StringUtility.SanitizeNumber(
      params.salesPackageSpeciesId,
    );
    this.salesPackageSpeciesName = params.salesPackageSpeciesName;
    this.salesPackageDisplayName = params.salesPackageDisplayName;
    this.salesPackageDescription = params.salesPackageDescription;
    this.salesPackageCode = params.salesPackageCode;
    this.salesPackageHelpPhotoId = StringUtility.SanitizeNumber(
      params.salesPackageHelpPhotoId,
    );
    this.salesPackageSortOrder = StringUtility.SanitizeNumber(
      params.salesPackageSortOrder,
    );
    this.salesPackageExpirationDays = StringUtility.SanitizeNumber(
      params.salesPackageExpirationDays,
    );
    this.salesPackageVaccineReminderDefaultDays = StringUtility.SanitizeNumber(
      params.salesPackageVaccineReminderDefaultDays,
    );
    this.isSalesPackageRefunded = params.isSalesPackageRefunded;
    this.visitCompletionType = new VisitCompletionTypeModel(
      params.visitCompletionType,
    );
    this.serviceAmount = params.serviceAmount;
    this.productAmount = params.productAmount;
    this.licenseFeeAmount = params.licenseFeeAmount;
    this.vetcoPrescriptionAmount = params.vetcoPrescriptionAmount;
    this.discountAmount = params.discountAmount;
    this.taxPercentage = params.taxPercentage;
    this.subtotalPreDiscountsAmount = params.subtotalPreDiscountsAmount;
    this.subtotalPostDiscountsAmount = params.subtotalPostDiscountsAmount;
    this.taxAmount = params.taxAmount;
    this.refundAmount = params.refundAmount;
    this.taxableTotalAmount = params.taxableTotalAmount;
    this.nonTaxableTotalAmount = params.nonTaxableTotalAmount;
    this.totalAmount = params.totalAmount;
    this.servicesOnlyDiscountAmount = params.servicesOnlyDiscountAmount;
    this.productsOnlyDiscountAmount = params.productsOnlyDiscountAmount;
    this.servicesRebateAmount = StringUtility.SanitizeNumber(
      params.servicesRebateAmount,
    );
    this.productsRebateAmount = StringUtility.SanitizeNumber(
      params.productsRebateAmount,
    );
    this.totalRebateAmount = StringUtility.SanitizeNumber(
      params.totalRebateAmount,
    );
    this.nonTaxableFeeAmount = StringUtility.SanitizeNumber(
      params.nonTaxableFeeAmount,
    );
    this.taxableFeeAmount = StringUtility.SanitizeNumber(
      params.taxableFeeAmount,
    );
    this.totalFeeAmount = StringUtility.SanitizeNumber(params.totalFeeAmount);
    this.totalAOVAmount = StringUtility.SanitizeNumber(params.totalAOVAmount);
    this.veterinarianId = StringUtility.SanitizeNumber(params.veterinarianId);
    this.veterinarianFirstName = params.veterinarianFirstName;
    this.veterinarianLastName = params.veterinarianLastName;
    this.veterinarianLicenseNumber = params.veterinarianLicenseNumber;
    this.veterinarianLicenseStateId = StringUtility.SanitizeNumber(
      params.veterinarianLicenseStateId,
    );
    this.veterinarianLicenseStateAbbreviation =
      params.veterinarianLicenseStateAbbreviation;
    this.veterinarianFullName = params.veterinarianFullName;
    this.veterinarianLicenseExpirationDate = StringUtility.SanitizeDateTimeISO(
      params.veterinarianLicenseExpirationDate,
    );
    this.veterinarianEmail = params.veterinarianEmail;
    this.dispensingAuthUserId = StringUtility.SanitizeNumber(
      params.dispensingAuthUserId,
    );
    this.dispensingUserFirstName = StringUtility.SanitizeString(
      params.dispensingUserFirstName,
    );
    this.dispensingUserLastName = StringUtility.SanitizeString(
      params.dispensingUserLastName,
    );
    this.thankYouNote = params.thankYouNote;
    this.storeStateNote = params.storeStateNote;
    this.clientStateNote = params.clientStateNote;
    this.visitTagId = StringUtility.SanitizeNumber(params.visitTagId);
    this.reservationId = StringUtility.SanitizeNumber(params.reservationId);
    this.reservation = params.reservation
      ? new ReservationModel(params.reservation)
      : undefined;
    this.examResults = (params.examResults ?? []).map(
      (e) => new VisitExamResultModel(e),
    );
    this.visitNotes = (params.visitNotes ?? []).map(
      (n) => new VisitNoteModel(n),
    );
    this.hasEvaluatedAutoRecommendations =
      params.hasEvaluatedAutoRecommendations;
    this.intakeId = StringUtility.SanitizeNumber(params.intakeId);
    this.intake = new IntakeModel(params.intake);
    this.arrived = StringUtility.SanitizeDateTimeISO(params.arrived);
    this.firstCheckIn = StringUtility.SanitizeDateTimeISO(params.firstCheckIn);
    this.visitStarted = StringUtility.SanitizeDateTimeISO(params.visitStarted);
    this.arrivedWaitingTime = StringUtility.SanitizeNumber(
      params.arrivedWaitingTime,
    );
    this.checkInWaitingTime = StringUtility.SanitizeNumber(
      params.checkInWaitingTime,
    );
    this.intakeCompletionStatusId = StringUtility.SanitizeNumber(
      params.intakeCompletionStatusId,
    );
    this.needsProactiveFleaTickPrescription = StringUtility.SanitizeBool(
      params.needsProactiveFleaTickPrescription,
    );
    this.appointmentType = params.appointmentType
      ? new AppointmentTypeModel(params.appointmentType)
      : undefined;
    this.reasonForVisit = StringUtility.SanitizeString(params.reasonForVisit);
    this.notes = StringUtility.SanitizeString(params.notes);
    this.dateCreated = DateTime.fromISO(params.dateCreated, { zone: 'utc' });
    this.dateUpdated = DateTime.fromISO(params.dateUpdated, { zone: 'utc' });
    this.visitAttachments = (params.visitAttachments ?? []).map(
      (v) => new VisitAttachmentModel(v),
    );
    this.consents = (params.consents ?? []).map(
      (c) => new VisitConsentModel(c),
    );
    this.visitSectionVersions = (params.visitSectionVersions ?? []).map(
      (vsv) => new VisitSectionVersionModel(vsv),
    );
    this.labOrders = (params.labOrders ?? []).map(
      (lo) => new LabOrderModel(lo),
    );
    this.anesthesiaForms = (params.anesthesiaForms ?? []).map(
      (af) => new VisitAnesthesiaFormModel(af),
    );
    this.hospitalPrescriptions = (params.hospitalPrescriptions ?? []).map(
      (hp) => new HospitalPrescriptionModel(hp),
    );
    this.isInAdminMode = params.isInAdminMode;
    this.audioRecordingUsed = params.audioRecordingUsed;
    this.freeNonPackageSalesItems = (params.freeNonPackageSalesItems ?? []).map(
      (si) => new VisitSalesItemModel(si, this.visitDate),
    );
    this.addOnSalesItems = (params.addOnSalesItems ?? []).map(
      (si) => new VisitSalesItemModel(si, this.visitDate),
    );
    this.paidNonPackageSalesItems = (params.paidNonPackageSalesItems ?? []).map(
      (si) => new VisitSalesItemModel(si, this.visitDate),
    );
    this.discountAndRebateSalesItems = (
      params.discountAndRebateSalesItems ?? []
    ).map((si) => new VisitSalesItemModel(si, this.visitDate));
    this.referrals = (params.referrals ?? []).map(
      (rf) => new VisitReferralModel(rf),
    );
    this.followUpBookingURL = params.followUpBookingURL;
    this.hasDetailedAdjustments = _.defaultTo(
      params.hasDetailedAdjustments,
      false,
    );
    this.petPreviousVisits = params.petPreviousVisits;
    makeObservable(this);
  }

  @action.bound
  filterVisitNotesByVisitNoteType = (
    visitNoteType: VisitNoteType | undefined,
  ): VisitNoteModel[] | undefined => {
    return this.visitNotes?.filter((v) => v.visitNoteTypeId == visitNoteType);
  };

  @action.bound getPetDiagnosesSpecifiedToVisit = (
    petDiagnoses: PetDiagnosisModel[],
    timeZone: string,
  ): PetDiagnosisModel[] => {
    if (!this.visitDate) return [];
    const allowedStatuses = [
      VisitStatus.Finalized,
      VisitStatus.Paid,
      VisitStatus.Check_Out,
    ];
    const currentVisitDate = this.visitDate.setZone(timeZone, {
      keepLocalTime: true,
    });
    const previousVisitsById = new Map(
      this.petPreviousVisits.map((v) => [v.visitId, v]),
    );
    return petDiagnoses.filter((pd) => {
      // converted to hospital zone
      const dateDiagnosedInHospitalTz = DateTime.fromISO(pd.dateDiagnosed, {
        zone: 'utc',
      }).setZone(timeZone);

      if (pd.visitId === this.visitId) return true;
      if (pd.visitId != null) {
        const prevVisit = previousVisitsById.get(pd.visitId);
        if (prevVisit) {
          const prevVisitDateTZ = DateTime.fromISO(prevVisit.visitDate, {
            zone: 'utc',
          }).setZone(timeZone, {
            keepLocalTime: true,
          });
          if (
            prevVisitDateTZ < currentVisitDate &&
            allowedStatuses.includes(prevVisit.visitStatusId)
          )
            return true;
        }
      }
      return pd.visitId == null && dateDiagnosedInHospitalTz < currentVisitDate;
    });
  };

  @action.bound
  hasVisitSummary = () =>
    this.visitSalesItems.some(
      (vsi) =>
        vsi.salesItemCommunicationTypeId === CommunicationType.VisitSummary,
    );

  @action.bound
  hasPrescription = () =>
    this.visitSalesItems.some(
      (vsi) =>
        vsi.salesItemCommunicationTypeId === CommunicationType.Prescription,
    );

  @action.bound
  hasRabiesCertificate = () =>
    this.visitSalesItems.some(
      (vsi) =>
        vsi.salesItemCommunicationTypeId ===
        CommunicationType.RabiesCertificate,
    );

  @action.bound
  hasDischargeInstructions = () =>
    this.visitWellnessExam.dischargeNotes !== undefined ||
    (this.visitNotes ?? []).some(
      (vn) => vn.visitNoteTypeId === VisitNoteType.Discharge,
    );

  @action.bound
  hasAnesthesiaMonitoring = () =>
    this.visitSalesItems.some(
      (vsi) =>
        vsi.salesItemCommunicationTypeId ===
        CommunicationType.AnesthesiaMonitoring,
    );

  @action.bound
  isVetTechExam = () => {
    return (
      ReservationAssignmentStatuses.Assigned_Technician ===
      this.reservation?.reservationAssignmentStatusId
    );
  };

  @action.bound
  hasHospitalPrescriptions = () =>
    this.hospitalPrescriptions.length > 0 || this.isRxOnly === true;

  @computed get isVisitLocked(): boolean {
    return (
      !this.isInAdminMode &&
      this.visitStatus.visitStatusId === VisitStatus.Check_Out
    );
  }

  @computed get hasUserSelectedItems(): boolean {
    return (this.visitSalesItems ?? []).some(
      (vsi) => !vsi.isAutoAddedToVisit(),
    );
  }

  @computed get canConvertToRxOnly(): boolean {
    return !this.isVisitLocked && !this.hasUserSelectedItems;
  }

  @action.bound
  getFollowUpAppointmentInfo = () => {
    return new FollowUpAppointmentModel({
      followUpBookingURL: this.followUpBookingURL!,
      clinetFirstname: this.client?.firstName!,
      clinetLastName: this.client?.lastName!,
      clinetCellPhone: this.client?.cellPhone!,
      clinetEmailAddress: this.client?.emailAddress!,
      petname: this.pet?.name!,
      petspecies: this.pet?.species?.name!,
      store_number: '',
      clinicZipCode: '',
      isreturningcustomer: 'true',
    });
  };

  @action.bound
  updatePetDetails = (pet: PetModel | undefined) => {
    if (this.pet && pet) {
      this.pet = pet;
    }
  };

  @action.bound
  getVisitSalesItemDisplayPrice(visitSalesItemId: number): number {
    const visitSalesItem = this.visitSalesItems?.find(
      (item) => item.visitSalesItemId === visitSalesItemId,
    );

    if (!visitSalesItem) {
      return 0;
    }

    if (visitSalesItem.wasDeclinedByClient) {
      return 0;
    }

    let salesItemPrice = visitSalesItem.salesItemPrice ?? 0;

    if (
      !this.hasDetailedAdjustments &&
      (visitSalesItem.salesItemSalesItemTypeId ===
        SalesItemType.CouponDiscountMonetary ||
        visitSalesItem.salesItemSalesItemTypeId ===
          SalesItemType.CouponDiscountPercentage)
    ) {
      salesItemPrice = this.discountAmount ?? 0;
    }

    if (
      this.hasDetailedAdjustments &&
      DiscountItemTypes.includes(visitSalesItem.salesItemSalesItemTypeId)
    ) {
      salesItemPrice = visitSalesItem.invoicePrice ?? 0;
    }

    return salesItemPrice;
  }

  @computed get salesPackageHasLoweredPrice(): boolean {
    return (
      this.salesPackageInvoicePrice != null &&
      this.salesPackageInvoicePrice < (this.salesPackagePrice ?? 0)
    );
  }

  @computed get hasSentConfirmationSMS(): boolean {
    return !!this.visitEvent?.confirmationSMSSent;
  }

  @computed get hasSentConsult1SMS(): boolean {
    return !!this.visitEvent?.consultationStart;
  }

  @computed get hasSentConsult2SMS(): boolean {
    return !!this.visitEvent?.consultation2Start;
  }

  @computed get inPackageItems(): VisitSalesItemModel[] {
    return this.visitSalesItems?.filter((i) => i.isInPackage) || [];
  }

  @computed get hasPackage(): boolean {
    return !!this.salesPackageId && !this.hasOptedOutOfPackage;
  }

  @computed get addOnItems(): VisitSalesItemModel[] {
    return (
      this.visitSalesItems?.filter(
        (i) => i.isPackageAddOn === true && !i.wasDeclinedByVeterinarian,
      ) || []
    );
  }

  @computed get paidNonPackageItems(): VisitSalesItemModel[] {
    return (
      this.visitSalesItems?.filter(
        (i) =>
          !i.isInPackage &&
          !i.isPackageAddOn &&
          !i.wasDeclinedByVeterinarian &&
          i.salesItemPrice > 0 &&
          !i.IsDiscountItem,
      ) || []
    );
  }

  @computed get freeNonPackageItems(): VisitSalesItemModel[] {
    return (
      this.visitSalesItems?.filter(
        (i) =>
          !i.isInPackage &&
          !i.isPackageAddOn &&
          !i.wasDeclinedByVeterinarian &&
          i.salesItemPrice === 0 &&
          !i.IsDiscountItem,
      ) || []
    );
  }

  @computed get discountAndRebateItems(): VisitSalesItemModel[] {
    return (
      this.visitSalesItems?.filter(
        (i) =>
          !i.isInPackage &&
          !i.isPackageAddOn &&
          !i.wasDeclinedByVeterinarian &&
          i.IsDiscountItem,
      ) || []
    );
  }

  @computed get addOnSalesItemsTotal(): number {
    return this.calculateVisitSalesItemsTotal(this.addOnItems);
  }

  @computed get paidNonPackageItemsTotal(): number {
    return this.calculateVisitSalesItemsTotal(this.paidNonPackageItems);
  }

  @computed get discountAndRebateItemsTotal(): number {
    return this.calculateVisitSalesItemsTotal(this.discountAndRebateItems);
  }

  @computed get packageTotalAmount(): number {
    return (
      (this.salesPackageInvoicePrice ?? this.salesPackagePrice ?? 0) +
      this.addOnSalesItemsTotal
    );
  }

  private calculateVisitSalesItemsTotal(
    items: VisitSalesItemModel[] | undefined,
  ): number {
    if (!items || items.length === 0) {
      return 0;
    }

    return items.reduce((sum, item) => {
      if (!item || item.wasDeclinedByClient) {
        return sum;
      }

      let price = item.invoicePriceOrSalesPrice ?? 0;
      const isDiscountOrRebateType = DiscountItemTypes.includes(
        item.salesItemSalesItemTypeId,
      );

      if (
        !this.hasDetailedAdjustments &&
        (item.salesItemSalesItemTypeId ===
          SalesItemType.CouponDiscountMonetary ||
          item.salesItemSalesItemTypeId ===
            SalesItemType.CouponDiscountPercentage)
      ) {
        price = this.discountAmount ?? 0;
      } else if (this.hasDetailedAdjustments && isDiscountOrRebateType) {
        price = item.invoicePrice ?? 0;
      }

      return sum + price;
    }, 0);
  }
}
